import React, { useState, useEffect, useRef } from 'react';
import { searchCompanies } from '../services/companies';
import './CompanySearchSelect.css';

export default function CompanySearchSelect({ onSelect, selectedCompany, placeholder = 'Search for a company...' }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(selectedCompany || null);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelected(selectedCompany || null);
  }, [selectedCompany]);

  useEffect(() => {
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim() === '') {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await searchCompanies(searchTerm, 10);
        setResults(data);
      } catch (error) {
        console.error('Error searching companies:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (company) => {
    setSelected(company);
    setSearchTerm(company.name);
    setShowDropdown(false);
    if (onSelect) {
      onSelect(company);
    }
  };

  const handleClear = () => {
    setSelected(null);
    setSearchTerm('');
    setResults([]);
    if (onSelect) {
      onSelect(null);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    if (selected) {
      setSelected(null);
      if (onSelect) {
        onSelect(null);
      }
    }
  };

  return (
    <div className="company-search-select" ref={dropdownRef}>
      <div className="search-input-wrapper">
        <input
          type="text"
          className="search-input"
          placeholder={placeholder}
          value={selected ? selected.name : searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          autoComplete="off"
        />
        {selected && (
          <button
            type="button"
            className="clear-button"
            onClick={handleClear}
            title="Clear selection"
          >
            ×
          </button>
        )}
        {loading && <div className="search-spinner"></div>}
      </div>

      {showDropdown && searchTerm.trim() !== '' && (
        <div className="search-dropdown">
          {loading ? (
            <div className="dropdown-item loading">Searching...</div>
          ) : results.length === 0 ? (
            <div className="dropdown-item empty">No companies found</div>
          ) : (
            results.map((company) => (
              <div
                key={company.id}
                className="dropdown-item"
                onClick={() => handleSelect(company)}
              >
                {company.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
